import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { getReports, getOceanReports, dispatchBulletinManual } from "../../services/reports";
import { Spin, TableColumnProps, Modal, message } from "antd";
import { ArrowDownToLine, Trash2, Mail } from "lucide-react";
import api from "../../services/api";
import { useState } from "react";
import useAuth from "../../hooks/useAuth";
import isAllowed from "../../utils/isAllowed";

/**
 * Determines if a country/section combo uses ocean (Copernicus) or acoustic bulletins.
 * Any sectioned route or known ocean country is treated as ocean.
 */
const isOceanBulletin = (country: string, section?: string): boolean => {
  if (section) return true;
  if (["peru", "ecuador", "panama", "oman"].includes(country)) return true;
  return false;
};

const stripFilenameAccents = (filename: string): string =>
  filename.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const getAttachmentFilename = (contentDisposition?: string): string | undefined => {
  if (!contentDisposition) return undefined;

  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      return encodedMatch[1];
    }
  }

  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1];
};

export const downloadReportFile = async (
  country: string,
  report: string,
  isOcean: boolean
) => {
  const basePath = isOcean
    ? `/report/ocean/${country}/${report}`
    : `/report/${country}/${report}`;

  const response = await api.get(basePath, { responseType: "blob" });

  const filename = stripFilenameAccents(
    getAttachmentFilename(response.headers["content-disposition"]) || report
  );

  const blob = new Blob([response.data], { type: response.headers["content-type"] });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const SECTION_LABELS: Record<string, Record<string, string>> = {
  peru:    { "1": "Perú — Boletín 1",    "2": "Perú — Boletín v3" },
  ecuador: { "1": "Ecuador — Boletín 1", "2": "Ecuador — Boletín v3" },
  oman:    { "1": "Omán — Boletín 1",    "2": "Omán — Boletín v3" },
  panama:  { "1": "Panamá — Boletín 1",  "2": "Panamá — Boletín v3" },
};

const COUNTRY_LABELS: Record<string, string> = {
  peru: "Perú", ecuador: "Ecuador", oman: "Omán", panama: "Panamá",
};

const normalizeBulletins = (raw: any): any[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.bulletins)) return raw.bulletins;
  if (Array.isArray(raw.items)) return raw.items;
  if (Array.isArray(raw.data)) return raw.data;
  return [];
};

function useReports() {
  const { country, section } = useParams();
  const [downloading, setDownloading] = useState<any>({});
  const { rolePermissions, userData } = useAuth();

  // Admin: tipo admin/super_admin/principal OR tiene permiso ui:bulletin:delete
  // Se verifica por tipo primero para no depender del seed de permisos nuevos
  const isAdmin =
    ["admin", "super_admin", "principal"].includes(userData?.type ?? "") ||
    isAllowed({
      permissions: rolePermissions ?? [],
      permission: "ui:bulletin:delete",
    });

  const ocean = isOceanBulletin(country || "", section);

  const { data: rawData, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["reports", country, section, ocean ? "ocean" : "acoustic"],
    queryFn: () => {
      if (ocean) return getOceanReports(country || "", section);
      return getReports(country || "");
    },
    staleTime: 30_000,
  });

  const tableData = normalizeBulletins(rawData).map((item: any) => ({
    name: item.filename || item.name,
    createdAt: item.created || item.createdAt,
    size: item.size,
    key: item.filename || item.name,
  }));

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleDownload = async (name: string) => {
    try {
      setDownloading({ [`report-${name}`]: true });
      await downloadReportFile(country || "", name, ocean);
    } catch (err) {
      console.error("Error downloading file:", err);
    } finally {
      setDownloading({ [`report-${name}`]: false });
    }
  };

  const handleDelete = (name: string) => {
    Modal.confirm({
      title: "¿Eliminar boletín?",
      content: (
        <span>
          Se eliminará permanentemente <strong>{name}</strong>.<br />
          Esta acción no se puede deshacer.
        </span>
      ),
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          setDownloading({ [`delete-${name}`]: true });
          await api.delete(`/report/ocean/${country}/${name}`);
          message.success("Boletín eliminado correctamente");
          refetch();
        } catch (err: any) {
          const detail = err?.response?.data?.detail || "Error al eliminar el boletín";
          message.error(detail);
        } finally {
          setDownloading({ [`delete-${name}`]: false });
        }
      },
    });
  };

  const handleGenerate = async (startDate?: string, endDate?: string) => {
    try {
      setDownloading({ generating: true });

      if (ocean) {
        let url = `/report/ocean/generate?country=${country}`;
        if (section) url += `&section=${section}`;

        const response = await api.post(url);
        const { job_id } = response.data || {};

        if (job_id) {
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await api.get(`/report/ocean/jobs/${job_id}`);
              const { status } = statusRes.data || {};
              if (status === "done" || status === "error") {
                clearInterval(pollInterval);
                setDownloading({ generating: false });
                refetch();
              }
            } catch {
              clearInterval(pollInterval);
              setDownloading({ generating: false });
            }
          }, 15_000);

          setTimeout(() => {
            clearInterval(pollInterval);
            setDownloading({ generating: false });
            refetch();
          }, 35 * 60 * 1000);

          return; // Keep "generating" state while polling
        }
        refetch();
      } else {
        let url = `/report/generate?country=${country}`;
        if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
        await api.post(url);
        refetch();
      }
    } catch (err) {
      console.error("Error generating bulletin:", err);
    } finally {
      if (!ocean) setDownloading({ generating: false });
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────────

  const tableColumns: TableColumnProps[] = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Fecha",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      defaultSortOrder: "descend",
      render: (val: string) => {
        if (!val) return "-";
        try {
          return new Date(val).toLocaleString("es-PE", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
          });
        } catch {
          return val;
        }
      },
    },
    {
      title: "Tamaño",
      dataIndex: "size",
      key: "size",
      render: (val: number) => {
        if (!val) return "-";
        if (val >= 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(1)} MB`;
        if (val >= 1024) return `${(val / 1024).toFixed(0)} KB`;
        return `${val} B`;
      },
    },
    {
      title: "Acciones",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* ── Descargar ── */}
          <Spin spinning={!!downloading[`report-${record.name}`]}>
            <div
              id={`report-${record.name}`}
              title="Descargar"
              style={{
                background: "#81BC3D",
                borderRadius: "50%",
                width: 28, height: 28,
                display: "flex", justifyContent: "center", alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => handleDownload(record.name)}
            >
              <ArrowDownToLine color="white" width={15} />
            </div>
          </Spin>

          {/* ── Eliminar — solo admins con ui:bulletin:delete ── */}
          {isAdmin && ocean && (
            <Spin spinning={!!downloading[`delete-${record.name}`]}>
              <div
                id={`delete-${record.name}`}
                title="Eliminar boletín (solo admin)"
                style={{
                  background: "#ff4d4f",
                  borderRadius: "50%",
                  width: 28, height: 28,
                  display: "flex", justifyContent: "center", alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleDelete(record.name)}
              >
                <Trash2 color="white" width={14} />
              </div>
            </Spin>
          )}

          {/* ── Enviar por email — solo admins ── */}
          {isAdmin && (
            <Spin spinning={!!downloading[`email-${record.name}`]}>
              <div
                id={`email-${record.name}`}
                title="Enviar boletín por email a todos los suscriptores"
                style={{
                  background: "#1890ff",
                  borderRadius: "50%",
                  width: 28, height: 28,
                  display: "flex", justifyContent: "center", alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  Modal.confirm({
                    title: "¿Enviar boletín por email?",
                    content: (
                      <span>
                        Se enviará <strong>{record.name}</strong> por email a todos los suscriptores con el permiso correspondiente.
                      </span>
                    ),
                    okText: "Enviar",
                    cancelText: "Cancelar",
                    onOk: async () => {
                      try {
                        setDownloading({ [`email-${record.name}`]: true });
                        const result = await dispatchBulletinManual(
                          country || "",
                          record.name,
                          section,
                          ocean ? "ocean" : "acoustic"
                        );
                        if (result.status === "warning") {
                          message.warning(result.message || "No se encontraron suscriptores");
                        } else {
                          message.success(
                            `Boletín enviado a ${result.recipients || 0} suscriptor(es)`
                          );
                        }
                      } catch (err: any) {
                        const detail = err?.response?.data?.error || "Error al enviar el boletín";
                        message.error(detail);
                      } finally {
                        setDownloading({ [`email-${record.name}`]: false });
                      }
                    },
                  });
                }}
              >
                <Mail color="white" width={14} />
              </div>
            </Spin>
          )}
        </div>
      ),
    },
  ];

  // ── Page title ───────────────────────────────────────────────────────────────

  const countryKey = country || "";
  const sectionKey = section || "";
  const pageTitle =
    SECTION_LABELS[countryKey]?.[sectionKey] ||
    COUNTRY_LABELS[countryKey] ||
    (countryKey ? countryKey.charAt(0).toUpperCase() + countryKey.slice(1) : "Boletines");

  return {
    country,
    section,
    isOcean: ocean,
    isAdmin,
    pageTitle,
    data: tableData,
    downloading,
    error,
    isFetching,
    isLoading,
    handleGenerate,
    refetch,
    tableColumns,
  };
}

export default useReports;
